// One search for Discover: the combined search's ranked list for the query, as title keys. Apart from the hook
// (useDiscoverSearch.ts), so that a test can run it without React.
import type { ReadingChip } from "~/server/combined-search/reading-retrieval.server"
import type { SearchFilters } from "~/server/combined-search/search-filters"
import type { SearchBatch } from "~/server/combined-search/search.server"
import { requestSearch } from "~/ui/search/search-client"
import { type TitleKey, titleKey } from "~/utils/title-key"

const UNAVAILABLE = "Search is unavailable right now. Try again in a moment."

export interface DiscoverSearchResult {
	q: string
	/** The ranked list in the ranking's order. */
	ranked: TitleKey[]
	/** For a topic search: what each of the search's own filters keeps out of the topic. */
	filterHides?: SearchBatch["filterHides"]
	/** Shared server snapshot; absent when the shared cache is unavailable. */
	searchSet?: string
	reading: ReadingChip[]
	people: SearchBatch["people"]
	creditScope: SearchBatch["creditScope"]
	/** Non-empty when the basic search served, or a part of the search failed. */
	errors: string[]
	pendingTitleLookup?: boolean
	/** Set on a partial list of title and people matches sent ahead of the reading and the ranking. */
	pendingRanking?: boolean
}

const rankedKeys = (batch: SearchBatch): TitleKey[] => {
	const keys: TitleKey[] = []
	for (const row of batch.rows) {
		const [type, id] = row.key.split(":")
		const tmdbId = Number(id)
		if ((type === "movie" || type === "show") && Number.isSafeInteger(tmdbId))
			keys.push(titleKey(type, tmdbId))
	}
	return [...new Set(keys)]
}

const resultFor = (q: string, batch: SearchBatch): DiscoverSearchResult => ({
	q,
	ranked: batch.searchKeys
		? (batch.searchKeys as TitleKey[])
		: rankedKeys(batch),
	...(batch.searchSet ? { searchSet: batch.searchSet } : {}),
	...(batch.filterHides ? { filterHides: batch.filterHides } : {}),
	reading: batch.reading ?? [],
	people: batch.people ?? [],
	creditScope: batch.creditScope ?? null,
	errors: batch.errors ?? [],
	pendingTitleLookup: batch.pendingTitleLookup,
	...(batch.pendingRanking ? { pendingRanking: true } : {}),
})

/**
 * Rejects with the message that Discover shows next to Retry: the server's own, or the busy message when the proxy
 * answered instead of a search role (see search-request.ts).
 */
export async function runDiscoverSearch(
	q: string,
	filters: SearchFilters,
	allTitles: boolean,
	signal: AbortSignal,
	onReading: (chips: ReadingChip[]) => void,
	onProgress?: (result: DiscoverSearchResult) => void,
	language?: string,
): Promise<DiscoverSearchResult> {
	const batch = await requestSearch({
		language,
		// discover: the frozen ranked candidate set, which the results endpoint filters and counts.
		body: { q, filters, allTitles, discover: true },
		signal,
		onReading,
		onProgress: (batch) => onProgress?.(resultFor(q, batch)),
		unavailable: UNAVAILABLE,
	})
	return resultFor(q, batch)
}
